import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Box, Button, Paper, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { SparklesIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Avatar } from "@outpave/ds";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import { GET_INTEGRATION } from "../../../graphql/integration";
import { getDomain, logoLink } from "components/common/RenderMerchantDisplay";
import { useBrandingContext } from "context/BrandingContext";
import { useColorScheme } from "context/ColorSchemeContext";
import { useSymbolForScheme } from "utils/themeContextHelper";
import { useCategories } from "../../../hooks/useCategories";
import { useAccountCategories } from "../../../hooks/useAccountCategories";
import { useUpdateCategories } from "../../../hooks/useUpdateCategories";
import ExpenseCategorySearchAutoComplete from "../../common/CategorySearchAutoComplete";

interface MapYourCategoriesDrawerProps {
  open: boolean;
  integrationType?: string;
  handleCloseDrawer: () => void;
  /** Where to put the buttons: the host drawer's footer. */
  footerHost?: HTMLElement | null;
}

export interface MappedCategories {
  id: string;
  name: string;
  level: number;
  accountCategory: string;
  accountSystem: string;
  accountCategoryId: string;
  accountCategorySearchTerm: string;
}

const MapYourCategoriesDrawer: React.FC<MapYourCategoriesDrawerProps> = (
  props,
) => {
  const { open, integrationType, handleCloseDrawer, footerHost } = props;
  const { t } = useTranslation();

  const { refetchOutpaveCategories, outpaveCategories } = useCategories();
  const { accountCategories, refetchAccountCategories } =
    useAccountCategories();
  const { selectedItems, setSelectedItems, updateCategories } =
    useUpdateCategories(accountCategories);
  const symbol = useSymbolForScheme();
  const { platform } = useBrandingContext();
  const { isDark } = useColorScheme();

  const { data: integrationData } = useQuery(GET_INTEGRATION, {
    variables: { integrationType },
    skip: !integrationType,
  });
  const integration = integrationData?.integration;
  const integrationDomain = integration?.url ? getDomain(integration.url) : "";
  const integrationLogo = integrationDomain
    ? logoLink(
        integrationDomain,
        "icon",
        "lettermark",
        isDark ? "dark" : "light",
        28,
      )
    : undefined;

  const onCloseDrawer = () => {
    // Revert the selections back to their original values
    setSelectedItems({});
    handleCloseDrawer();
  };

  useEffect(() => {
    if (open) {
      refetchAccountCategories();
      refetchOutpaveCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Stack gap="var(--space-8)" sx={{ padding: "var(--space-6)" }}>
        {/* Elevated rather than surface: a Paper on a drawer body resolves to
            the same fill, so the notice would not read as a panel at all. The
            radius is the themed Paper's own. */}
        <Paper
          elevation={0}
          sx={{
            padding: "var(--space-4)",
            backgroundColor: "var(--color-bg-elevated)",
          }}
        >
          <Inline gap="var(--space-4)" align="start" wrap={false}>
            {/* The row is flex, so the mark gives up width to the text beside
                it and renders narrower than it is tall, unless told not to. */}
            <Box sx={{ display: "flex", flexShrink: 0 }}>
              <HugeiconsIcon
                icon={SparklesIcon}
                size={16}
                color="var(--color-text-brand)"
              />
            </Box>
            <Typography variant="body2">
              {t("quickbooksMappingPrompt")}
            </Typography>
          </Inline>
        </Paper>

        {/* One grid, not three: the headers and the rows only lined up before
            because every cell happened to carry the same padding. */}
        <Columns
          count={2}
          gap="var(--space-7)"
          sx={{ columnGap: "var(--space-9)", alignItems: "center" }}
        >
          <Inline gap="var(--space-3)" wrap={false} sx={{ minWidth: 0 }}>
            <Avatar
              src={symbol}
              name={platform.name}
              alt={platform.name}
              size="md"
              variant="rounded"
            />
            <Typography variant="subtitle2">{platform.name}</Typography>
          </Inline>
          <Inline gap="var(--space-3)" wrap={false} sx={{ minWidth: 0 }}>
            <Avatar
              src={integrationLogo}
              name={integration?.title ?? ""}
              alt={integration?.title ?? ""}
              size="md"
              variant="rounded"
            />
            <Typography variant="subtitle2">{integration?.title}</Typography>
          </Inline>

          <Typography variant="label">{t("Outpave categories")}</Typography>
          <Typography variant="label">
            {t("QuickBooks chart of accounts")}
          </Typography>

          {outpaveCategories.map((category) => {
            const transformedCategory = {
              ...category,
              id: category.accountCategoryId,
              name: category.accountCategory,
            };

            return (
              <React.Fragment key={category.id}>
                {/* Separators off: a category name is tenant data, and a period
                    in it would otherwise be read as a key path. */}
                <Typography variant="body1">
                  {t(category.name, {
                    nsSeparator: false,
                    keySeparator: false,
                  })}
                </Typography>
                <ExpenseCategorySearchAutoComplete
                  expenseCategory={
                    selectedItems[category.id] || transformedCategory
                  }
                  onSelectExpenseCategory={(selectedCategory) => {
                    selectedCategory &&
                      setSelectedItems({
                        ...selectedItems,
                        [category.id]: selectedCategory,
                      });
                  }}
                  hideClearButton={true}
                  size="md"
                />
              </React.Fragment>
            );
          })}
        </Columns>
      </Stack>
      {footerHost &&
        createPortal(
          <>
            <Button
              variant="primary-outlined"
              onClick={() => {
                onCloseDrawer();
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              disabled={Object.keys(selectedItems).length === 0}
              onClick={async () => {
                await updateCategories();
                handleCloseDrawer();
                refetchAccountCategories();
                refetchOutpaveCategories();
              }}
            >
              {t("Save")}
            </Button>
          </>,
          footerHost,
        )}
    </>
  );
};

export default MapYourCategoriesDrawer;
